import React, { useEffect, useState } from "react";

// 3rd Party libraries
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Divider } from "@mui/material";
import moment from "moment";

// Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import CustomImage from "components/Table/GroupImage";
import FullScreenImageComponent from "components/ViewFullImage/ViewImage";

// Examples component
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageTitle from "examples/NewDesign/PageTitle";
import BasicButton from "examples/NewDesign/CustomButton/BasicButton";
import DataTable from "examples/Tables/DataTable";
import BasicModal from "examples/modal/BasicModal/BasicModal";
import BasicModal2 from "examples/modal/BasicModal/BasicModal2";
import CheckInCheckOutComments from "examples/modal/CheckInCheckOutComments/CheckInCheckOutComments";
import AddRequestEquipment from "layouts/projectOrders/ProjectEquipment/AddRequestEquipment";

// Layout
import ProjectOrderHistoryDetailsData from "layouts/projectOrders/data/ProjectOrderStatus/ProjectOrderHistoryDetailsData";
import ProjectOrderHistoryEquipmentData from "layouts/projectOrders/data/ProjectOrderStatus/ProjectOrderHistoryEquipmentData";

// Redux
import { openSnackbar } from "redux/Slice/Notification";
import {
  getProjectOrderHistoryOrderDetailsById,
  equipmentOrderAdjustmentThunk,
  addItemBatchToRequestedOrderThunk,
} from "redux/Thunks/EquipmentRequest";

// Constants
import Constants, {
  Icons,
  Colors,
  defaultData,
  ButtonTitles,
  ModalContent,
  Common,
} from "utils/Constants";
import pxToRem from "assets/theme/functions/pxToRem";
import { getFormattedCallingFirstName } from "utils/methods/methods";
import Status from "components/Table/Status";

// Assets
import User from "assets/images/UserDefault.png";

const boxStylesForGridView = {
  flex: "1 1 calc(25% - 16px)", // Ensures 4 items per row
  minWidth: "150px", // Prevents boxes from shrinking too much
  display: "flex",
  flexDirection: "column",
  alignItems: "left",
  justifyContent: "left",
};

const cardBoxLabelStyle = {
  fontWeight: 400,
  fontSize: pxToRem(14),
  color: "#475467",
};

const cardBoxValueStyle = {
  fontWeight: 600,
  fontSize: pxToRem(14),
  color: "#191A51",
  textTransform: "capitalize",
};

const getDisplayValue = (value) => {
  if (value === 0) return 0;
  if (value === null || value === undefined) return Constants.EMPTY_VALUE;
  if (typeof value === "string" && value.trim() === "") return Constants.EMPTY_VALUE;
  return value;
};

const ORDER_ADJUSTMENT_ADD_ITEM_DISABLED_STATUSES = new Set(
  Common.PROJECT_ORDER_ADJUSTMENT_BLOCKED_STATUSES
);

const normalizeStatus = (status = "") =>
  `${status || ""}`.trim().toLowerCase().replace(/_/g, "-").replace(/\s+/g, "-");

function ProjectOrderHistoryOrderByDetails() {
  const dispatch = useDispatch();

  const location = useLocation();
  const {
    orderId = null,
    projectId = "",
    projectName,
    orderId: stateOrderId,
  } = location.state || {};

  const { projectOrderByOrderIdData = {}, projectOrderByOrderIdLoading } = useSelector(
    (state) => state.equipmentRequest
  );

  const [wmCommentsData, setWMCommentsData] = useState({
    open: false,
    type: "",
    comments: [],
  });
  const [rejectRemarkData, setRejectRemarkData] = useState({
    open: false,
    type: "",
    comments: [],
  });
  // States
  const [fullScreenImage, setFullScreenImage] = useState(null);
  const [orderByDetailsObj, setOrderByDetailsObj] = useState({});
  const [shouldUpdateState, setShouldUpdateState] = useState(false);
  const [equipmentTypeList, setEquipmentTypeList] = useState([]);
  const [openEquipmentDetailsModal, setOpenEquipmentDetailsModal] = useState(false);

  const orderByName =
    orderByDetailsObj?.orderBy && Object.keys(orderByDetailsObj?.orderBy || {}).length > 0
      ? getFormattedCallingFirstName(orderByDetailsObj?.orderBy)
      : Constants.EMPTY_VALUE;

  const orderDuration =
    orderByDetailsObj?.shoppingCartData?.fromDate && orderByDetailsObj?.shoppingCartData?.toDate
      ? `${moment(orderByDetailsObj?.shoppingCartData?.fromDate).format(
          defaultData.WEB_DATE_FORMAT_WITH_SLASH
        )} -${moment(orderByDetailsObj?.shoppingCartData?.toDate).format(
          defaultData.WEB_DATE_FORMAT_WITH_SLASH
        )}`
      : Constants.EMPTY_VALUE;
  const isAddItemDisabledByOrderStatus = ORDER_ADJUSTMENT_ADD_ITEM_DISABLED_STATUSES.has(
    normalizeStatus(orderByDetailsObj?.orderStatus)
  );
  const [adjustmentLoading, setAdjustmentLoading] = useState(false);
  const [openAddEquipmentModal, setOpenAddEquipmentModal] = useState(false);
  const [showOrderAdjustActions, setShowOrderAdjustActions] = useState(false);
  const [adjustmentQuantityMap, setAdjustmentQuantityMap] = useState({});
  const [addRequestedEquipmentObj, setAddRequestedEquipmentObj] = useState({
    equipmentOrders: [],
    tempOrders: [],
  });
  const [addRequestedEquipmentLoading, setAddRequestedEquipmentLoading] = useState(false);

  const getManageOrderId = (item = {}) =>
    item?.pmManageOrderId || item?.[Constants.MONGOOSE_ID] || "";

  const getOrderAdjustmentId = () =>
    stateOrderId ||
    orderId ||
    orderByDetailsObj?.orderId ||
    orderByDetailsObj?.equipmentTypeData?.[0]?.pmOrder ||
    orderByDetailsObj?.[Constants.MONGOOSE_ID] ||
    "";

  const resetOrderAdjustMode = () => {
    setShowOrderAdjustActions(false);
    setAdjustmentQuantityMap({});
  };

  const handleCloseAddEquipmentModal = () => {
    setAddRequestedEquipmentObj({
      equipmentOrders: [],
      tempOrders: [],
    });
    setOpenAddEquipmentModal(false);
  };

  const handleAddEquipmentFunc = async () => {
    if (
      addRequestedEquipmentObj.equipmentOrders.length === 0 &&
      addRequestedEquipmentObj.tempOrders.length === 0
    ) {
      dispatch(
        openSnackbar({
          message: Constants.ENGINEER_REQUESTED_ITEM_CREATE_ERROR,
          notificationType: Constants.NOTIFICATION_ERROR,
        })
      );
      return;
    }

    setAddRequestedEquipmentLoading(true);

    const transformedPayload = {
      ...addRequestedEquipmentObj,
      equipmentOrders: addRequestedEquipmentObj.equipmentOrders.map((item) => ({
        ...item,
        pmComment: item.engineerComment,
        engineerComment: undefined,
      })),
      tempOrders: addRequestedEquipmentObj.tempOrders.map((item) => ({
        ...item,
        pmComment: item.engineerComment,
        engineerComment: undefined,
      })),
    };

    try {
      const addItemOrderId = getOrderAdjustmentId();

      if (!addItemOrderId) {
        setAddRequestedEquipmentLoading(false);
        dispatch(
          openSnackbar({
            message: Constants.SOMETHING_WENT_WRONG,
            notificationType: Constants.NOTIFICATION_ERROR,
          })
        );
        return;
      }

      const res = await dispatch(
        addItemBatchToRequestedOrderThunk({
          id: addItemOrderId,
          body: transformedPayload,
        })
      );

      if (res?.payload?.status === Common.API_STATUS_200) {
        setAddRequestedEquipmentLoading(false);
        handleCloseAddEquipmentModal();
        dispatch(
          openSnackbar({
            message: Constants.ENGINEER_REQUESTED_ITEM_CREATE_SUCCESS,
            notificationType: Constants.NOTIFICATION_SUCCESS,
          })
        );
        setShouldUpdateState((prev) => !prev);
      } else if (res?.payload?.status === Common.API_STATUS_400) {
        setAddRequestedEquipmentLoading(false);
        dispatch(
          openSnackbar({
            message: res?.payload?.data?.data?.[0]?.error || Constants.SOMETHING_WENT_WRONG,
            notificationType: Constants.NOTIFICATION_ERROR,
          })
        );
      } else {
        setAddRequestedEquipmentLoading(false);
        dispatch(
          openSnackbar({
            message: Constants.SOMETHING_WENT_WRONG,
            notificationType: Constants.NOTIFICATION_ERROR,
          })
        );
      }
    } catch (error) {
      setAddRequestedEquipmentLoading(false);
      dispatch(
        openSnackbar({
          message: Constants.SOMETHING_WENT_WRONG,
          notificationType: Constants.NOTIFICATION_ERROR,
        })
      );
    }
  };

  const handleStartOrderAdjust = () => {
    const initializedQuantities =
      orderByDetailsObj?.equipmentTypeData?.reduce((acc, item, index) => {
        const rowKey = item?.rowKey || `${item?.typeName || "equipment"}-${index}`;
        return {
          ...acc,
          [rowKey]: {
            pmManageOrderId: getManageOrderId(item),
            originalQuantity: Number(item?.pmRequestedQuantity || 0),
            adjustedQuantity: Number(item?.pmRequestedQuantity || 0),
          },
        };
      }, {}) || {};

    setAdjustmentQuantityMap(initializedQuantities);
    setShowOrderAdjustActions(true);
  };

  const handleAdjustmentQuantity = (rowKey, type) => {
    setAdjustmentQuantityMap((prev) => {
      const existing = prev?.[rowKey];
      if (!existing) return prev;

      const currentQty = Number(existing.adjustedQuantity || 0);
      let updatedQty = currentQty;
      if (type === "increment") {
        updatedQty = currentQty + 1;
      } else if (type === "decrement") {
        updatedQty = Math.max(0, currentQty - 1);
      }

      return {
        ...prev,
        [rowKey]: {
          ...existing,
          adjustedQuantity: updatedQty,
        },
      };
    });
  };

  const handleAdjustmentQuantityInput = (rowKey, value) => {
    const sanitizedValue = `${value || ""}`.replace(Common.NON_DIGIT_REGEX, "");
    const parsedQty = sanitizedValue === "" ? 0 : Number(sanitizedValue);

    setAdjustmentQuantityMap((prev) => {
      const existing = prev?.[rowKey];
      if (!existing) return prev;

      return {
        ...prev,
        [rowKey]: {
          ...existing,
          adjustedQuantity: Number.isFinite(parsedQty) ? parsedQty : 0,
        },
      };
    });
  };

  const handleApplyAdjustment = async () => {
    const changedEquipment = Object.values(adjustmentQuantityMap).filter(
      (item) => Number(item.adjustedQuantity || 0) !== Number(item?.originalQuantity || 0)
    );

    if (changedEquipment.length === 0) {
      resetOrderAdjustMode();
      return;
    }

    const body = {
      changes: changedEquipment.map((item) => ({
        pmManageOrderId: item?.pmManageOrderId || "",
        newQuantity: Number(item.adjustedQuantity || 0),
      })),
    };

    const hasInvalidManageOrderId = body.changes.some((item) => !item.pmManageOrderId);
    if (hasInvalidManageOrderId) {
      dispatch(
        openSnackbar({
          message: Constants.ORDER_ADJUSTMENT_INVALID_MANAGE_ORDER_ID,
          notificationType: Constants.NOTIFICATION_ERROR,
        })
      );
      return;
    }

    const adjustmentId = getOrderAdjustmentId();
    if (!adjustmentId) {
      dispatch(
        openSnackbar({
          message: Constants.SOMETHING_WENT_WRONG,
          notificationType: Constants.NOTIFICATION_ERROR,
        })
      );
      return;
    }

    setAdjustmentLoading(true);
    const res = await dispatch(
      equipmentOrderAdjustmentThunk({
        id: adjustmentId,
        body,
      })
    );
    setAdjustmentLoading(false);

    if (res?.payload?.status === 200) {
      const successMessage = res?.payload?.data?.message || Constants.STATUS_UPDATE_SUCCESS;
      dispatch(
        openSnackbar({
          message: successMessage,
          notificationType: Constants.NOTIFICATION_SUCCESS,
        })
      );
      resetOrderAdjustMode();
      setShouldUpdateState((prev) => !prev);
    } else {
      const errorMessage = res?.payload?.data?.message || Constants.SOMETHING_WENT_WRONG;
      dispatch(
        openSnackbar({
          message: errorMessage,
          notificationType: Constants.NOTIFICATION_ERROR,
        })
      );
    }
  };
  // Table Functions
  const handleViewDetailsClick = (equipmentList) => {
    setEquipmentTypeList(equipmentList);
    setOpenEquipmentDetailsModal(true);
  };

  const handleCloseEquipmentModal = () => {
    setOpenEquipmentDetailsModal(false);
    setEquipmentTypeList([]);
  };

  const handleImageFullView = (imageUrl) => {
    setFullScreenImage(imageUrl);
  };

  const handleCloseFullView = () => {
    setFullScreenImage(null);
  };

  const openWarehouseCommentModal = (type, intialComments) => {
    const { open, ...rest } = wmCommentsData;
    setWMCommentsData({
      ...rest,
      open: true,
      type,
      comments: [...(intialComments.pmComments || []), ...(intialComments.wmComments || [])],
    });
  };

  const openRejectCommentModal = (type, remark) => {
    const { open, ...rest } = rejectRemarkData;
    setRejectRemarkData({
      ...rest,
      open: true,
      type,
      comments: [remark],
    });
  };

  // Table Columns
  const { projectHistoryByIdColumns, projectHistoryByIdRows } = ProjectOrderHistoryDetailsData({
    dataList: orderByDetailsObj?.equipmentTypeData || [],
    handleViewDetailsClick,
    openWarehouseCommentModal,
    openRejectCommentModal,
    showOrderAdjustActions,
    adjustmentQuantityMap,
    handleAdjustmentQuantity,
    handleAdjustmentQuantityInput,
    adjustmentLoading,
  });

  // Equipment Type Table Column Displayed in Modal
  const { equipmentTypeColumn, equipmentTypeRows } = ProjectOrderHistoryEquipmentData({
    dataList: equipmentTypeList,
  });

  const getProjectOrderHistoryOrderDetailsByIdFunc = async (id) => {
    try {
      const payload = {
        id,
      };
      await dispatch(getProjectOrderHistoryOrderDetailsById(payload));
    } catch (error) {
      dispatch(
        openSnackbar({
          message: Constants.SOMETHING_WENT_WRONG,
          notificationType: Constants.NOTIFICATION_ERROR,
        })
      );
    }
  };

  useEffect(() => {
    if (orderId) {
      getProjectOrderHistoryOrderDetailsByIdFunc(orderId);
    }
  }, [orderId, shouldUpdateState]);

  useEffect(() => {
    const { headerInfo = {}, orderDetails = [] } = projectOrderByOrderIdData;

    const normalizedOrder = {
      [Constants.MONGOOSE_ID]: projectOrderByOrderIdData?.[Constants.MONGOOSE_ID],
      orderId:
        projectOrderByOrderIdData?.[Constants.MONGOOSE_ID] ||
        projectOrderByOrderIdData?.orderId ||
        orderDetails?.[0]?.pmOrder ||
        "",
      orderNumber: headerInfo?.orderNo,
      orderBy: headerInfo?.orderBy,
      totalApprovedItems: headerInfo?.totalItems,
      totalApprovedQuantity: headerInfo?.totalWmApprovedQuantity,
      orderStatus: headerInfo?.status,
      shoppingCartData: {
        fromDate: headerInfo?.fromDate,
        toDate: headerInfo?.toDate,
      },
      totalRequestedQuantity: headerInfo?.totalRequestedQuantity,
      equipmentTypeData: (orderDetails || []).map((item, index) => ({
        ...item,
        rowKey:
          getManageOrderId(item) || `${item?.equipmentType?.name?.trim() || "equipment"}-${index}`,
        typeName: item?.equipmentType?.name?.trim(),
        comments: {
          pmComments: item?.pmComments || [],
          wmComments: item?.wmComments || [],
        },
        remark: item?.remark || [],
      })),
    };

    setOrderByDetailsObj(normalizedOrder);
  }, [projectOrderByOrderIdData]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox display="flex" justifyContent="space-between">
        <PageTitle title={projectName || ""} />
        <MDBox mt={{ lg: 0, sm: 2 }} display="flex" flexWrap="wrap" gap={1}>
          {!showOrderAdjustActions ? (
            <BasicButton
              title="Order Adjust"
              icon={Icons.EDIT}
              background={Colors.WHITE}
              border
              color={Colors.BLACK}
              action={handleStartOrderAdjust}
            />
          ) : (
            <>
              <BasicButton
                title={ButtonTitles.CANCEL}
                background={Colors.WHITE}
                border
                color={Colors.BLACK}
                disabled={adjustmentLoading}
                action={resetOrderAdjustMode}
              />
              <BasicButton
                title={ButtonTitles.ADD_ITEM}
                icon={Icons.ADD}
                background={Colors.WHITE}
                border
                color={Colors.BLACK}
                disabled={adjustmentLoading || isAddItemDisabledByOrderStatus}
                action={() => setOpenAddEquipmentModal(true)}
              />
              <BasicButton
                title={ButtonTitles.PLACE_ORDER}
                background={Colors.PRIMARY}
                color={Colors.WHITE}
                disabled={adjustmentLoading}
                action={handleApplyAdjustment}
              />
            </>
          )}
          <BasicButton
            icon={Icons.RELOAD}
            background={Colors.WHITE}
            border
            color={Colors.BLACK}
            action={() => setShouldUpdateState((prev) => !prev)}
          />
        </MDBox>
      </MDBox>

      <Divider sx={{ marginTop: 2 }} />

      <MDBox display="flex" flexDirection="column" gap={2} mt={2}>
        <MDBox
          display="flex"
          flexWrap="wrap"
          bgColor="white"
          gap={2}
          border="1px solid #E0E6F5"
          borderRadius="8px"
          padding="16px 24px"
          minHeight="140px"
        >
          <MDBox sx={boxStylesForGridView}>
            <MDTypography sx={cardBoxLabelStyle}>Order No.</MDTypography>
            <MDTypography sx={cardBoxValueStyle}>
              {getDisplayValue(orderByDetailsObj?.orderNumber)}
            </MDTypography>
          </MDBox>

          <MDBox sx={boxStylesForGridView}>
            <MDTypography sx={cardBoxLabelStyle}>Order By</MDTypography>
            <MDBox display="flex" alignItems="center">
              <MDBox onClick={() => handleImageFullView(User)} sx={{ cursor: "pointer" }}>
                <CustomImage item={User} width={30} height={30} />
              </MDBox>
              <MDTypography sx={cardBoxValueStyle}>{getDisplayValue(orderByName)}</MDTypography>
            </MDBox>
          </MDBox>

          <MDBox sx={boxStylesForGridView}>
            <MDTypography sx={cardBoxLabelStyle}>Total Items</MDTypography>
            <MDTypography sx={cardBoxValueStyle}>
              {getDisplayValue(orderByDetailsObj?.totalApprovedItems)}
            </MDTypography>
          </MDBox>

          <MDBox sx={boxStylesForGridView}>
            <MDTypography sx={cardBoxLabelStyle}>Total WM Approved Quantity</MDTypography>
            <MDTypography sx={cardBoxValueStyle}>
              {getDisplayValue(orderByDetailsObj?.totalApprovedQuantity)}
            </MDTypography>
          </MDBox>

          <MDBox sx={boxStylesForGridView}>
            <MDTypography sx={cardBoxLabelStyle}>Status</MDTypography>
            <MDTypography sx={cardBoxValueStyle} mt={0.4}>
              {orderByDetailsObj?.orderStatus ? (
                <Status title={`${orderByDetailsObj?.orderStatus?.replace("-", " ")}`} />
              ) : (
                Constants.EMPTY_VALUE
              )}
            </MDTypography>
          </MDBox>

          {/* <MDBox sx={boxStylesForGridView}>
            <MDTypography sx={cardBoxLabelStyle}>Order Date</MDTypography>
            <MDTypography sx={cardBoxValueStyle}>
              {moment(orderByDetailsObj?.shoppingCartData?.fromDate).format("DD/MM/YYYY")} -
              {moment(orderByDetailsObj?.shoppingCartData?.toDate).format("DD/MM/YYYY")}
            </MDTypography>
          </MDBox> */}
          <MDBox sx={boxStylesForGridView}>
            <MDTypography sx={cardBoxLabelStyle}>Order Duration</MDTypography>
            <MDTypography sx={cardBoxValueStyle}>{orderDuration}</MDTypography>
          </MDBox>

          <MDBox sx={boxStylesForGridView}>
            <MDTypography sx={cardBoxLabelStyle}>Total Requested Quantity</MDTypography>
            <MDTypography sx={cardBoxValueStyle}>
              {getDisplayValue(orderByDetailsObj?.totalRequestedQuantity)}
            </MDTypography>
          </MDBox>
          <MDBox sx={boxStylesForGridView} visibility="hidden">
            It will keep it align
          </MDBox>
        </MDBox>

        <MDBox>
          <DataTable
            table={{ columns: projectHistoryByIdColumns, rows: projectHistoryByIdRows }}
            isSorted={false}
            entriesPerPage={{ defaultValue: defaultData.PER_PAGE_4 }}
            showTotalEntries={false}
            pagination={{ variant: "gradient", color: "info" }}
            loading={projectOrderByOrderIdLoading}
            licenseRequired
          />
        </MDBox>

        {/* Equipment Type Modal */}
        <BasicModal
          title="Equipment Details"
          open={openEquipmentDetailsModal}
          handleClose={handleCloseEquipmentModal}
          displayButtons={false}
        >
          <DataTable
            backgroundColor={Colors.LIGHT_GRAY}
            textColor={Colors.BLACK}
            table={{ columns: equipmentTypeColumn, rows: equipmentTypeRows }}
            isSorted={false}
            entriesPerPage={{ defaultValue: defaultData.PER_PAGE_4 }}
            showTotalEntries={false}
            pagination={{ variant: "gradient", color: "info" }}
            loading={projectOrderByOrderIdLoading}
            licenseRequired
          />
        </BasicModal>

        {/* Add Request Equipment Modal */}
        <BasicModal2
          open={openAddEquipmentModal}
          handleClose={handleCloseAddEquipmentModal}
          title={ModalContent.REQUEST_ITEMS_MODAL_TITLE}
          actionButton={ButtonTitles.SUBMIT}
          btnLoading={addRequestedEquipmentLoading}
          btnLoadingText={ButtonTitles.SUBMIT_LOADING}
          disabled={addRequestedEquipmentLoading}
          handleAction={handleAddEquipmentFunc}
          width="90%"
          isAdditionalBtnRequired
          additionalBtnTitle={ButtonTitles.CANCEL}
          additionalBtnAction={handleCloseAddEquipmentModal}
          additionalBtnDisabled={addRequestedEquipmentLoading}
        >
          <AddRequestEquipment
            addRequestedEquipmentObj={addRequestedEquipmentObj}
            setAddRequestedEquipmentObj={setAddRequestedEquipmentObj}
            defaultProjectId={projectId}
          />
        </BasicModal2>

        {/* Order History Comments */}
        {wmCommentsData.open && (
          <CheckInCheckOutComments
            open={wmCommentsData.open}
            type={wmCommentsData.type}
            intialComments={wmCommentsData.comments}
            handleClose={() =>
              setWMCommentsData({
                open: false,
                type: "",
                comments: [],
              })
            }
            minWidth={600}
          />
        )}

        {rejectRemarkData.open && (
          <CheckInCheckOutComments
            open={rejectRemarkData.open}
            type={rejectRemarkData.type}
            intialComments={rejectRemarkData.comments}
            handleClose={() =>
              setRejectRemarkData({
                open: false,
                type: "",
                comments: [],
              })
            }
            minWidth={600}
          />
        )}

        {/* View Order By Profile */}
        <FullScreenImageComponent
          fullScreenImage={fullScreenImage}
          handleCloseFullView={handleCloseFullView}
          src={fullScreenImage}
        />
      </MDBox>
    </DashboardLayout>
  );
}

export default ProjectOrderHistoryOrderByDetails;
